<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：Copyright
 *
 * 作成者：2021/12/02 ノア）虞
 * 最終修正者：2022/01/26 ノア）虞
 *
 -->
<template>
  <div class="locus-body">
    <div class="map-header">
      <!-- ヘッダー -->
      <section>
        <nav class="tti-main-header-shadow">
          <div class="pull-left">
            <el-button class="backButton" @click="onDoBack" v-tooltip="getTooltip(tooltipType.backTip)">
              <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
            </el-button>
            <span class="tti-viewTitle">
              <tti-svg-icon icon-name="wo_014"></tti-svg-icon>
              {{ $t('menu.locusDisplay') }}
              <span class="ml-12">
                <el-select
                  class="tti-viewSelect"
                  @change="onChangeTargetId"
                  v-model="managementTargetId"
                  placeholder=""
                  size="small"
                  v-tooltip="{
                    content: `${$t('dynamicHistory.selectTargetTip', [managementTarget])}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                >
                  <el-option
                    v-for="item in dynamicTargetMasterData"
                    :key="item.manageTargetId"
                    :label="item.manageTargetName + '(' + item.manageTargetCode + ')'"
                    :value="item.manageTargetId"
                  >
                  </el-option>
                </el-select>
              </span>
              <span class="ml-12">{{ targetNameForPage }}</span>
            </span>
          </div>
          <div class="pull-right">
            <el-button class="tti-btn-jump" @click="onDoSearch" v-tooltip="getTooltip(tooltipType.searchTip)">
              <tti-svg-icon icon-name="su_016"> </tti-svg-icon>{{ $t('buttons.search') }}
              <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
            </el-button>
          </div>
        </nav>
      </section>
    </div>
    <div class="map-body">
      <div class="map-slider">
        <div class="map-left" v-show="IsSliderShowAll">
          <div class="slider-details">
            <el-form :model="aggregateSelection" label-width="100px" class="aggregateSelectionForm">
              <el-form-item prop="phoneNumber" :label="$t('operationalPerformance.operationStartDay')">
                <el-date-picker
                  v-model="aggregateSelection.operationStartDay"
                  type="datetime"
                  size="small"
                  format="yyyy/MM/dd HH:mm:ss"
                  value-format="yyyy/MM/dd HH:mm:ss"
                  :picker-options="pickerOptions"
                  :editable="false"
                  :clearable="false"
                  v-tooltip="{
                    content: `${$t('operationalPerformance.operationStartDayTip')}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
              <el-form-item prop="name" :label="$t('operationalPerformance.operationEndDay')">
                <el-date-picker
                  v-model="aggregateSelection.operationEndDay"
                  type="datetime"
                  size="small"
                  format="yyyy/MM/dd HH:mm:ss"
                  value-format="yyyy/MM/dd HH:mm:ss"
                  :picker-options="pickerOptions"
                  :editable="false"
                  :clearable="false"
                  v-tooltip="{
                    content: `${$t('operationalPerformance.operationEndDayTip')}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
            </el-form>
            <div class="date-btn">
              <!-- 前日 -->
              <el-button
                class="tti-btn-001"
                icon="el-icon-arrow-left"
                size="mini"
                @click="dateTimeChange(DateChangeType.PREVIOUS_DAY)"
                v-tooltip="getTooltip(tooltipType.previousDayTip)"
              ></el-button>
              <!-- 翌日 -->
              <el-button
                class="tti-btn-001"
                icon="el-icon-arrow-right"
                size="mini"
                @click="dateTimeChange(DateChangeType.NEXT_DAY)"
                v-tooltip="getTooltip(tooltipType.nextDayTip)"
              ></el-button>
            </div>

            <!-- 表で詳しく示す -->
            <div>
              <el-table
                :data="locusData"
                border
                :empty-text="$t('locationInfo.noData')"
                header-row-class-name="tti-master-table-header"
                class="slider-table"
                highlight-current-row
                @current-change="onCurrentChange"
              >
                <el-table-column :label="$t('dynamicHistory.number')" width="50" align="center">
                  <template slot-scope="scope">
                    <span>{{ indexMethod(scope.row, scope.$index) }}</span>
                  </template>
                </el-table-column>
                <el-table-column
                  prop="deviceStatusName"
                  :label="$t('dynamicHistory.status')"
                  width="105"
                ></el-table-column>
                <el-table-column
                  :sortable="true"
                  :sort-orders="['ascending', 'descending']"
                  prop="startDate"
                  :label="$t('dynamicHistory.startTime')"
                  align="left"
                >
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
        <div
          class="map-right"
          :class="{
            allMapRightLeft: !IsSliderShowAll
          }"
          @click="onChangeSlider"
        >
          <tti-svg-icon icon-name="su_037" iconWidth="12"></tti-svg-icon>
        </div>
      </div>
      <!-- 地図 -->
      <div ref="mapContainer" class="zdc-map"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'

import {
  General,
  DynamicTargetParam,
  DynamicTargetMaster,
  DynamicHistory,
  DynamicHistoryParam,
  AggregateSelection,
  DynamicPlaceParam,
  ManagementPlaceMapData
} from '@/models'
import { MapService } from '@/commons/services/mapService'
import { generalService, GeneralType } from '@/commons/services/generalService'
import { common } from '@/store/modules'
import {
  getDynamicTargetMasterData,
  getDynamicHistoryData,
  getDynamicPlaceData,
  getDynamicPlaceDtlData
} from '@/store/apis'
import { getCurrentDate, getNextDay, getPreviousDay, isDateOver, pickerOptions } from '@/utils/dateUtils'
import HelperService from '@/service/helperService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { i18n } from '@/locales'
import { localService } from '@/commons/services/localService'

enum DateChangeType {
  /**
   * 前日
   */
  PREVIOUS_DAY = 'previousDay',
  /**
   * 翌日
   */
  NEXT_DAY = 'nextDay'
}

enum DateType {
  /**
   * 開始日
   */
  START = 'start',
  /**
   * 終了日
   */
  END = 'end'
}

@Component({
  name: 'map-locus'
})
export default class MAP_LOCUS extends Vue {
  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * 管理対象
   */
  managementTarget = localService.userInfo?.managementTargetShowName || i18n.t('commons.managementTarget')

  /**
   * サイドバー表示
   */
  IsSliderShowAll = true

  /**
   * 地図サービス
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapService: any

  /**
   * 管理対象ID
   */
  theManagementTargetId = ''

  /**
   * 管理対象ID
   *
   * @returns {void}
   */
  set managementTargetId(value: string) {
    this.theManagementTargetId = value
  }

  /**
   * 管理対象ID
   *
   * @returns {string} 管理対象ID
   */
  get managementTargetId(): string {
    return this.theManagementTargetId
  }

  /**
   * 管理対象ID
   */
  managementTargetIdCache = ''

  /**
   * 稼働状況リスト
   */
  operationStatusList: General[] = []

  /**
   * 動態管理対象マスタ
   */
  dynamicTargetMasterData: DynamicTargetMaster[] = []

  /**
   * 気に入り営業所
   *
   * @returns {string[]} 気に入り営業所
   */
  get watchedFavOfficeList(): string[] {
    return common.favOfficeList
  }

  /**
   * 管理場所一覧
   */
  theDynamicPlaceList: ManagementPlaceMapData[] = []

  /**
   * 移動履歴
   */
  locusData: DynamicHistory[] = []

  /**
   * 管理対象コード + 管理対象名称
   */
  targetNameForPage = ''

  /**
   * デフォルト展開状態
   */
  isShowNone = true

  selectedTarget: DynamicHistory | null = null

  /**
   * 集計期間情報
   */
  aggregateSelection: AggregateSelection = {
    operationStartDay: '',
    operationEndDay: ''
  }

  /**
   * マスタ画面ツールチップタイプ
   */
  tooltipType = {
    // 戻る
    backTip: 'backTip',
    // 指定日時
    dateTimeTip: 'dateTimeTip',
    // 前日
    previousDayTip: 'previousDayTip',
    // 翌日
    nextDayTip: 'nextDayTip',
    // 検索
    searchTip: 'searchTip'
  }

  DateChangeType = DateChangeType

  /**
   * 気に入り営業所変更した時の処理
   *
   * @param {string[]} value  気に入り営業所
   * @returns {void}
   */
  @Watch('watchedFavOfficeList', { deep: true })
  async onFavOfficeChange(value: string[]): Promise<void> {
    if (!value || value.length === 0) {
      return
    }
    this.initStartAndEndDate()

    const dynamicTargetParam = {
      businessOfficeIdList: value
    }
    const dynamicPlaceParam = {
      businessOfficeList: value
    }

    this.mapService = new MapService()

    const mapContainer = this.$refs.mapContainer
    if (!mapContainer) {
      return
    }
    this.mapService.init(mapContainer as HTMLDivElement)

    this.fetchDynamicTargetMasterList(dynamicTargetParam)
    await this.fetchMasterPlaceList(dynamicPlaceParam)
    this.fetchMasterPlaceDtlList(dynamicPlaceParam)
  }

  /**
   * カスタムインデックス
   *
   * @param {DynamicHistory} row 移動履歴
   * @param {number} index インデックス
   * @returns {number} ンデックス
   */
  indexMethod(row: DynamicHistory, index: number): number {
    row.number = index + 1
    return index + 1
  }

  /**
   * 管理場所一覧を取得する
   *
   * @param {DynamicPlaceParam} dynamicTargetParam 管理場所一覧のパラメータ
   * @returns {void}
   */
  async fetchMasterPlaceList(dynamicTargetParam: DynamicPlaceParam): Promise<void> {
    try {
      const dynamicPlaceData = await getDynamicPlaceData(dynamicTargetParam)
      this.theDynamicPlaceList = dynamicPlaceData.list as ManagementPlaceMapData[]

      this.formatPlaceListAfter(this.theDynamicPlaceList)

      this.$log.debug('MAP > fetchMasterPlaceList: dynamicPlaceData=', dynamicPlaceData)
    } catch (e) {
      this.$log.debug('MAP > fetchMasterPlaceList: e=', e)
      throw e
    }
  }

  /**
   * 管理場所データ変換された操作
   *
   * @param {ManagementPlaceMapData[]} dynamicPlaceList 管理場所データ
   */
  formatPlaceListAfter(dynamicPlaceList: ManagementPlaceMapData[]): void {
    dynamicPlaceList.forEach(item => {
      this.mapService.drawImage(item)
    })
  }

  /**
   * 管理場所詳細一覧を取得する
   *
   * @param {DynamicPlaceParam} dynamicTargetParam 管理場所一覧のパラメータ
   * @returns {void}
   */
  async fetchMasterPlaceDtlList(dynamicTargetParam: DynamicPlaceParam): Promise<void> {
    try {
      const dynamicPlaceData = await getDynamicPlaceDtlData(dynamicTargetParam)

      if (!dynamicPlaceData || !dynamicPlaceData.list) {
        return
      }

      ;(dynamicPlaceData.list as ManagementPlaceMapData[]).forEach(item => {
        this.mapService.drawImage(item)
      })

      this.$log.debug('MAP > fetchMasterPlaceList: dynamicPlaceData=', dynamicPlaceData)
    } catch (e) {
      this.$log.debug('MAP > fetchMasterPlaceList: e=', e)
      throw e
    }
  }

  /**
   * 動態管理対象マスタを取得する
   *
   * @param {DynamicTargetParam} dynamicTargetParam 動態管理対象のパラメータ
   * @returns {void}
   */
  async fetchDynamicTargetMasterList(dynamicTargetParam: DynamicTargetParam): Promise<void> {
    try {
      const dynamicTargetMaster = await getDynamicTargetMasterData(dynamicTargetParam)

      this.dynamicTargetMasterData = dynamicTargetMaster.list

      this.managementTargetId = this.getTargetId(this.managementTargetId, this.dynamicTargetMasterData)
      this.targetNameForPage = this.getTargetName(this.managementTargetId, this.dynamicTargetMasterData)
      this.fetchOperationStatus()

      this.managementTargetId && this.fetchDynamicHistoryData()

      this.$log.debug('MAP > fetchDynamicTargetMasterList: dynamicTargetMasterData=', dynamicTargetMaster)
    } catch (e) {
      this.$log.debug('MAP > fetchDynamicTargetMasterList: e=', e)
      throw e
    }
  }

  /**
   * デフォルト管理対象IDを取得
   *
   * @param {string} id 管理対象ID
   * @param {DynamicTargetMaster[]} masterData 動態管理対象マスタ
   * @returns {string} デフォルト管理対象ID
   */
  getTargetId(id: string, masterData: DynamicTargetMaster[]): string {
    if (!id) return ''

    let resultId = ''

    for (const iterator of masterData) {
      if (iterator.manageTargetId === id) {
        resultId = iterator.manageTargetId
        break
      }
    }

    return resultId
  }

  /**
   * 管理対象コード + 管理対象名称を取得
   *
   * @param {string} id 管理対象ID
   * @param {DynamicTargetMaster[]} masterData 動態管理対象マスタ
   * @returns {string} 管理対象コード + 管理対象名称
   */
  getTargetName(id: string, masterData: DynamicTargetMaster[]): string {
    if (!id) return ''

    let resultName = ''

    for (const iterator of masterData) {
      if (iterator.manageTargetId === id) {
        resultName = `${iterator.manageTargetName}(${iterator.manageTargetCode})`
        break
      }
    }

    return resultName
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    const startTime = this.aggregateSelection.operationStartDay
    const endTime = this.aggregateSelection.operationEndDay
    if (isDateOver(endTime, startTime, 7)) {
      msgBoxService.info(i18n.t('dynamicHistory.dateOverMessage', [7]).toString())
      return false
    }

    return true
  }

  /**
   * 管理対象変更後の処理
   *
   * @returns {void}
   */
  onChangeTargetId(): void {
    if (!this.correlationCheck()) {
      this.managementTargetId = this.managementTargetIdCache
      return
    }
    this.targetNameForPage = this.getTargetName(this.managementTargetId, this.dynamicTargetMasterData)
    this.fetchDynamicHistoryData()
  }

  /**
   * 検索ボタンを押した後の処理
   *
   * @returns {void}
   */
  onDoSearch(): void {
    if (!this.correlationCheck()) return
    this.fetchDynamicHistoryData()
  }

  /**
   * 移動履歴一覧取得する
   *
   * @returns {void}
   */
  async fetchDynamicHistoryData(): Promise<void> {
    try {
      const dynamicHistoryParam: DynamicHistoryParam = {
        managementTargetId: this.managementTargetId,
        startDate: this.aggregateSelection.operationStartDay,
        endDate: this.aggregateSelection.operationEndDay
      }

      const dynamicHistoryData = await getDynamicHistoryData(dynamicHistoryParam)
      this.locusData = dynamicHistoryData.list

      this.locusData.forEach((item, index) => {
        this.locusData[index].deviceStatusName = HelperService.getDefinitionName(
          this.operationStatusList,
          item.deviceStatus,
          { code: 'code', value: 'name' }
        )
        this.locusData[index].lat = HelperService.dmToDeg(item.lat as string)
        this.locusData[index].lng = HelperService.dmToDeg(item.lng as string)
      })

      this.managementTargetIdCache = this.managementTargetId

      const posData = cloneDeep(this.locusData)

      posData.sort((a, b) => {
        if (a.startDate > b.startDate) return 1
        if (a.startDate < b.startDate) return -1
        return 0
      })

      const positions = posData.filter(item => item.lat && item.lng).map(({ lat, lng }) => ({ lat, lng }))
      this.mapService.drawPolylineWithArrow(positions)
    } catch (e) {
      this.mapService.removePolylineWithArrow()
      this.$log.debug('MAP > fetchDynamicHistoryData: e=', e)
      throw e
    }
  }

  /**
   * URLから管理対象取得
   *
   * @returns {void}
   */
  initManagementTarget(): void {
    this.managementTargetId = this.$route.query.managementTargetId
      ? (this.$route.query.managementTargetId as string)
      : ''
  }

  /**
   * 稼働状況リストを取得する
   *
   * @returns {void}
   */
  async fetchOperationStatus(): Promise<void> {
    try {
      this.operationStatusList = await generalService[GeneralType.DEVICE_STATUS]
    } catch (e) {
      this.$log.debug('MAP > fetchOperationStatus: e=', e)
      throw e
    }
  }

  /**
   * メニューが隠れているか
   */
  onChangeSlider(): void {
    this.IsSliderShowAll = !this.IsSliderShowAll
    setTimeout(() => {
      this.mapService.mapRefresh()
    })
  }

  /**
   * 空白箇所を表示する必要があるか
   *
   * @returns {void}
   */
  onShowNoneClick(): void {
    this.isShowNone = !this.isShowNone
  }

  dateTimeChange(type: DateChangeType): void {
    if (!this.correlationCheck()) return

    if (type === DateChangeType.NEXT_DAY) {
      const startDateTime = getNextDay(this.aggregateSelection.operationStartDay, 'yyyy/MM/dd HH:mm:ss')
      const endDateTime = getNextDay(this.aggregateSelection.operationEndDay, 'yyyy/MM/dd HH:mm:ss')

      this.aggregateSelection.operationStartDay = startDateTime + ' 00:00:00'
      this.aggregateSelection.operationEndDay = endDateTime + ' 23:59:59'
    }

    if (type === DateChangeType.PREVIOUS_DAY) {
      const startDateTime = getPreviousDay(this.aggregateSelection.operationStartDay, 'yyyy/MM/dd HH:mm:ss')
      const endDateTime = getPreviousDay(this.aggregateSelection.operationEndDay, 'yyyy/MM/dd HH:mm:ss')

      this.aggregateSelection.operationStartDay = startDateTime + ' 00:00:00'
      this.aggregateSelection.operationEndDay = endDateTime + ' 23:59:59'
    }

    this.fetchDynamicHistoryData()
  }

  /**
   * 現在のシステム時間の範囲を取得する
   *
   * @param {DateType} dateType 開始日終了日
   * @returns {string} 現在のシステム時間の範囲
   */
  getDefaultTime(dateType: DateType): string {
    const currentDate = getCurrentDate('yyyy/MM/dd')

    const startDate = currentDate + ' 00:00:00'
    const endDate = currentDate + ' 23:59:59'

    let resultDate = ''

    if (dateType === DateType.START) {
      resultDate = startDate
    }

    if (dateType === DateType.END) {
      resultDate = endDate
    }

    return resultDate
  }

  /**
   * 時間変更後の処理
   *
   * @param {string[] | null} dateValue 時間
   * @param {DateType} dateType 開始日終了日
   */
  onChangeDate(dateValue: string[] | null, dateType: DateType): void {
    if (!dateValue) {
      if (dateType === DateType.START) {
        this.aggregateSelection.operationStartDay = this.getDefaultTime(dateType)
      }

      if (dateType === DateType.END) {
        this.aggregateSelection.operationEndDay = this.getDefaultTime(dateType)
      }
    }
  }

  initStartAndEndDate(): void {
    this.aggregateSelection.operationStartDay = this.getDefaultTime(DateType.START)
    this.aggregateSelection.operationEndDay = this.getDefaultTime(DateType.END)
  }

  onCurrentChange(val: DynamicHistory): void {
    this.selectedTarget = val

    this.mapService.markPlotPoint(this.selectedTarget)
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  onDoBack(): void {
    window.history.back()
  }

  /**
   * 画面ツールチップを取得する
   *
   * @param {string} key ツールチップキー
   * @returns {any} ツールチップ
   */
  // eslint-disable-next-line
  getTooltip(key: string): any {
    return {
      content: i18n.t(`dynamicHistory.${key}`),
      placement: 'bottom-start',
      delay: { show: 1000, hide: 0 }
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.initManagementTarget()
    this.onFavOfficeChange(this.watchedFavOfficeList)
  }
}
</script>

<style scoped lang="scss">
.locus-body {
  width: 100%;
  height: 100%;
}

.ml-12 {
  margin-left: 12px;
}

.tti-btn-jump {
  background-color: $C43;
  height: 100%;
  float: right;
  padding: 0;

  .svg-icon {
    padding: 0 6px;
  }

  &:active {
    @include bt_001_p;
  }
}

.map-header {
  .tti-main-header-shadow {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .map-header-second {
    width: 100%;
    height: 40px;
    box-shadow: -4px 1px 2px #777;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 11;

    .title-id {
      padding-left: 13px;
    }

    div:nth-child(1) {
      width: 340px;
    }

    div:nth-child(2) {
      display: flex;
      align-items: center;

      ::v-deep .el-date-editor {
        margin-right: 35px;
      }
    }

    div:nth-child(3) {
      display: flex;
      position: absolute;
      right: 15px;

      ::v-deep .el-select {
        margin-right: 10px;
      }
    }

    div:nth-child(2),
    div:nth-child(3) {
      button {
        box-shadow: 1px 1px 0 0 rgb(0 0 0 / 25%), 2px 2px 0 0 rgb(0 0 0 / 10%), -1px -1px 0 0 rgb(0 0 0 / 5%);
        height: 30px;
      }
    }
  }
}

.map-body {
  display: flex;
  width: 100%;
  height: calc(100vh - 88px);
}

.zdc-map {
  width: 100%;
  height: 100%;
}

.map-slider {
  height: 100%;
  z-index: 10;
  position: relative;
  display: flex;
  align-items: center;

  .map-left {
    width: 330px;
    height: 100%;
    border-right: 1px solid;
    transition: opacity 0.2s ease-in;
    background: #fff;

    .slider-details {
      width: 330px;

      ::v-deep .el-date-editor.el-input {
        width: 210px;
      }

      .slider-table {
        width: 100%;

        ::v-deep thead > tr > th {
          background: #e8e8e8;
          font-size: 12px;
          // font-weight: 400;
          color: #555;
          border-right: 1px solid #d4d4d4;
          border-bottom: 1px solid #d4d4d4;
        }

        ::v-deep .el-table__body-wrapper {
          max-height: calc(calc(100vh - 48px) - 245px);
          overflow: hidden;
          overflow-y: auto;
          font-size: $FS01;
        }

        ::v-deep .el-table__empty-text {
          width: 80%;
        }
      }
    }
  }

  .map-right {
    width: 12px;
    height: 48px;
    cursor: pointer;
    position: absolute;
    background: $C02;
    color: $C09;
    box-shadow: 2px 2px 2px rgb(0 0 0 / 50%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: -1px;
    left: 330px;
  }

  .allSliderRightLeft {
    width: 0;
  }

  .allMapRightLeft {
    left: 0;
  }
}

.el-form-item {
  margin-bottom: 8px;
}

.aggregateSelectionForm {
  margin-top: 16px;
}

.date-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}
</style>
